/*
  newcustom.js
  Custom homepage interactions for the M&H Living recreation.
*/

(function () {
  'use strict';

  /* ==
     LAYOUT VARS  (top bar + header heights → CSS vars on :root)
     == */
  function setLayoutVars() {
    var topbar = document.querySelector('.mh-topbar');
    var header = document.querySelector('[data-mh-header]');
    var root = document.documentElement;

    if (topbar) {
      root.style.setProperty('--mh-topbar-h', topbar.offsetHeight + 'px');
    }
    if (header) {
      root.style.setProperty('--mh-header-h', header.offsetHeight + 'px');
    }
  }

  /* ==
     TOP BAR SLIDER  (rotates between HTML blocks, e.g. sale banner + Trustpilot widget)
     == */
  function initTopBarSlider() {
    document.querySelectorAll('[data-mh-topbar-slider]').forEach(function (slider) {
      if (slider._mhTopbarBound) return;
      slider._mhTopbarBound = true;

      var slides = slider.querySelectorAll('.mh-topbar__slide');
      if (slides.length < 2) return;

      var interval = (parseFloat(slider.dataset.interval, 10) || 2) * 1000;
      var index = 0;
      var timer;
      var hovering = false;

      function show(next) {
        slides[index].classList.remove('is-active');
        slides[index].setAttribute('aria-hidden', 'true');
        index = (next + slides.length) % slides.length;
        slides[index].classList.add('is-active');
        slides[index].setAttribute('aria-hidden', 'false');
      }

      function start() {
        stop();
        timer = setInterval(function () { show(index + 1); }, interval);
      }
      function stop() {
        clearInterval(timer);
      }

      // Manual jump from a wheel/swipe gesture (see below); restarts the
      // auto-rotate timer unless the mouse is still hovering the bar.
      var gestureLocked = false;
      function advance(direction) {
        if (gestureLocked) return;
        gestureLocked = true;
        show(index + direction);
        if (!hovering) start();
        setTimeout(function () { gestureLocked = false; }, 500);
      }

      // Scrolling or swiping over the bar, in any direction, jumps to the
      // next/previous slide. Listeners stay passive (no preventDefault) so
      // normal page scrolling/swiping is never blocked.
      slider.addEventListener('wheel', function (e) {
        var delta = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
        if (Math.abs(delta) < 12) return;
        advance(delta > 0 ? 1 : -1);
      }, { passive: true });

      var touchX = 0;
      var touchY = 0;
      slider.addEventListener('touchstart', function (e) {
        touchX = e.touches[0].clientX;
        touchY = e.touches[0].clientY;
      }, { passive: true });
      slider.addEventListener('touchend', function (e) {
        var dx = e.changedTouches[0].clientX - touchX;
        var dy = e.changedTouches[0].clientY - touchY;
        var delta = Math.abs(dx) > Math.abs(dy) ? dx : dy;
        if (Math.abs(delta) < 24) return;
        advance(delta < 0 ? 1 : -1);
      }, { passive: true });

      start();
      slider.addEventListener('mouseenter', function () { hovering = true; stop(); });
      slider.addEventListener('mouseleave', function () { hovering = false; start(); });
      document.addEventListener('visibilitychange', function () {
        if (document.hidden) stop(); else if (!hovering) start();
      });
    });
  }

  /* ==
     HEADER  (initial → solid on scroll)
     == */
  function initHeader() {
    var header = document.querySelector('[data-mh-header]');
    if (!header) return;

    function setSolidState() {
      var threshold = parseInt(getComputedStyle(header).getPropertyValue('--mh-header-top') || '32', 10);
      header.classList.toggle('is-solid', window.scrollY > threshold);
    }

    setSolidState();
    window.addEventListener('scroll', setSolidState, { passive: true });
  }

  /* ==
     SUBMENUS  (click toggles for keyboard / touch; hover handled by CSS)
     == */
  function initSubmenus() {
    var triggers = document.querySelectorAll('.mh-header__menu-item--has-children .mh-header__menu-link');
    if (!triggers.length) return;

    function closeAll(except) {
      triggers.forEach(function (t) {
        if (t !== except) t.setAttribute('aria-expanded', 'false');
      });
    }

    triggers.forEach(function (link) {
      link.addEventListener('click', function (e) {
        var hasUrl = link.getAttribute('href') && link.getAttribute('href') !== '#';
        var expanded = link.getAttribute('aria-expanded') === 'true';

        if (window.matchMedia('(hover: hover)').matches && hasUrl) {
          return;
        }

        e.preventDefault();
        closeAll(link);
        link.setAttribute('aria-expanded', expanded ? 'false' : 'true');
      });
    });

    document.addEventListener('click', function (e) {
      if (!e.target.closest('.mh-header__menu-item--has-children')) closeAll();
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeAll();
    });
  }

  /* ==
     MOBILE DRAWER  (slide-in panel)
     == */
  function initDrawer() {
    var drawer = document.querySelector('[data-mh-drawer]');
    if (!drawer) return;

    var openTriggers  = document.querySelectorAll('[data-mh-drawer-open]');
    var closeTriggers = drawer.querySelectorAll('[data-mh-drawer-close]');
    var panel         = drawer.querySelector('.mh-drawer__panel');
    var lastFocus     = null;

    function open() {
      lastFocus = document.activeElement;
      drawer.hidden = false;
      requestAnimationFrame(function () { drawer.classList.add('is-open'); });
      drawer.setAttribute('aria-hidden', 'false');
      document.body.classList.add('mh-drawer-open');
      openTriggers.forEach(function (b) { b.setAttribute('aria-expanded', 'true'); });
      var focusable = panel && panel.querySelector('button, a, [tabindex]:not([tabindex="-1"])');
      if (focusable) focusable.focus();
    }

    function close() {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('mh-drawer-open');
      openTriggers.forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
      setTimeout(function () { drawer.hidden = true; }, 350);
      if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
    }

    openTriggers.forEach(function (b) { b.addEventListener('click', open); });
    closeTriggers.forEach(function (b) { b.addEventListener('click', close); });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) close();
    });
  }

  /* ==
     HERO  (respect prefers-reduced-motion → pause autoplay videos)
     == */
  function initHero() {
    if (!window.matchMedia) return;
    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    var videos  = document.querySelectorAll('.mh-hero__video');
    if (!videos.length) return;

    function apply() {
      videos.forEach(function (v) {
        if (reduced.matches) {
          try { v.pause(); v.removeAttribute('autoplay'); } catch (e) {}
        } else if (v.paused) {
          try { v.play(); } catch (e) {}
        }
      });
    }

    apply();
    if (typeof reduced.addEventListener === 'function') {
      reduced.addEventListener('change', apply);
    } else if (typeof reduced.addListener === 'function') {
      reduced.addListener(apply);
    }
  }

  /* ==
     CUSTOMER PICTURES GALLERY (M&H) — lightbox + drag-scroll
     == */
  function initCustomerGallery() {
    var openBtns = document.querySelectorAll('[data-mh-gallery-open]');
    if (!openBtns.length) return;

    var activeModal  = null;
    var lastFocused  = null;

    function openModal(id) {
      var modal    = document.getElementById('mh-gallery-' + id);
      if (!modal) return;
      var section  = modal.closest('.mh-cgal');
      var backdrop = section ? section.querySelector('[data-mh-gallery-backdrop]') : null;

      lastFocused  = document.activeElement;
      activeModal  = modal;

      modal.removeAttribute('hidden');
      if (backdrop) {
        backdrop.removeAttribute('hidden');
        backdrop.style.display = 'block';
      }

      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          modal.classList.add('is-visible');
          if (backdrop) backdrop.classList.add('is-visible');
        });
      });

      document.body.style.overflow = 'hidden';
      var closeBtn = modal.querySelector('[data-mh-gallery-close]');
      if (closeBtn) closeBtn.focus();
    }

    function closeModal() {
      if (!activeModal) return;
      var section  = activeModal.closest('.mh-cgal');
      var backdrop = section ? section.querySelector('[data-mh-gallery-backdrop]') : null;
      var modal    = activeModal;

      modal.classList.remove('is-visible');
      if (backdrop) backdrop.classList.remove('is-visible');

      setTimeout(function () {
        modal.setAttribute('hidden', '');
        if (backdrop) {
          backdrop.setAttribute('hidden', '');
          backdrop.style.display = '';
        }
      }, 240);

      activeModal = null;
      document.body.style.overflow = '';
      if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
    }

    openBtns.forEach(function (btn) {
      if (btn.dataset.mhGalleryBound === 'true') return;
      btn.dataset.mhGalleryBound = 'true';
      btn.addEventListener('click', function () {
        openModal(btn.dataset.mhGalleryOpen);
      });
    });

    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-mh-gallery-close]'))    closeModal();
      if (e.target.hasAttribute('data-mh-gallery-backdrop') ||
          e.target.closest('[data-mh-gallery-backdrop]') === e.target) {
        if (!e.target.closest('.mh-cgal__modal')) closeModal();
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && activeModal) closeModal();
    });

    /* Drag-to-scroll for mouse users */
    var scrollEls = document.querySelectorAll('[data-mh-gallery-scroll]');
    scrollEls.forEach(function (el) {
      if (el.dataset.mhScrollBound === 'true') return;
      el.dataset.mhScrollBound = 'true';

      var isDown   = false;
      var startX   = 0;
      var scrollLeft = 0;
      var moved    = false;

      el.addEventListener('mousedown', function (e) {
        isDown     = true;
        moved      = false;
        startX     = e.pageX - el.offsetLeft;
        scrollLeft = el.scrollLeft;
        el.classList.add('is-dragging');
      });

      el.addEventListener('mouseleave', function () {
        isDown = false;
        el.classList.remove('is-dragging');
      });

      el.addEventListener('mouseup', function () {
        isDown = false;
        el.classList.remove('is-dragging');
      });

      el.addEventListener('mousemove', function (e) {
        if (!isDown) return;
        e.preventDefault();
        moved        = true;
        var x        = e.pageX - el.offsetLeft;
        var walk     = (x - startX) * 1.2;
        el.scrollLeft = scrollLeft - walk;
      });

      /* Prevent click-through when drag happened */
      el.addEventListener('click', function (e) {
        if (moved) {
          e.stopPropagation();
          e.preventDefault();
          moved = false;
        }
      }, true);
    });
  }

  /* ==
     CUSTOMIZE FABRICS (M&H) — chip → palette interaction
     == */
  function initCustomizeFabrics() {
    document.querySelectorAll('[data-mh-cz-root]').forEach(function (root) {
      var chips = root.querySelectorAll('[data-mh-cz-chip]');
      if (!chips.length) return;

      chips.forEach(function (chip) {
        chip.addEventListener('click', function () {
          var idx = chip.dataset.mhCzChip;

          chips.forEach(function (c) {
            c.classList.toggle('mh-customize__chip--active', c === chip);
          });

          root.querySelectorAll('[data-mh-cz-group]').forEach(function (g) {
            g.classList.toggle('mh-customize__swatch-group--hidden', g.dataset.mhCzGroup !== idx);
          });
        });

        chip.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            chip.click();
          }
        });
      });
    });
  }

  function initBottomCta() {
    document.querySelectorAll('[data-mh-cta-atc]').forEach(function (btn) {
      if (btn.dataset.mhCtaBound === 'true') return;
      btn.dataset.mhCtaBound = 'true';
      btn.addEventListener('click', function () {
        var mainAtcBtn = document.querySelector('form[data-type="add-to-cart-form"] button[name="add"]');
        if (mainAtcBtn) mainAtcBtn.click();
      });
    });

    // Keep price display in sync with variant changes
    document.addEventListener('variant:changed', function (e) {
      var price = e.detail && e.detail.variant && e.detail.variant.price;
      if (!price) return;
      document.querySelectorAll('[data-mh-cta-price]').forEach(function (el) {
        el.textContent = ' — ' + window.Shopify.formatMoney(price, window.theme && window.theme.moneyFormat);
      });
    });
  }

  function initPdpMobileReorder() {
    var BREAKPOINT = 750;

    var whyLoved  = document.querySelector('.product-why-loved');
    var specs     = document.querySelector('.mh-specs');
    var buyBtns   = document.querySelector('.buy-buttons-block');
    var swatches  = document.querySelector('.order-free-swatches');

    if (!whyLoved || !specs || !buyBtns) return;

    // Free swatches sit directly under add-to-cart in the details column, so the
    // mobile blocks anchor after them rather than cutting in between.
    var mobileAnchor = (swatches && swatches.parentNode === buyBtns.parentNode) ? swatches : buyBtns;

    // Capture original positions once — these are live DOM references
    var whyLovedOrigParent = whyLoved.parentNode;
    var whyLovedOrigNext   = whyLoved.nextSibling;
    var specsOrigParent    = specs.parentNode;
    var specsOrigNext      = specs.nextSibling;

    var moved = false;

    function moveToMobile() {
      if (moved) return;
      // Insert after the anchor in order: anchor → whyLoved → specs
      // Insert specs first, then whyLoved before it
      mobileAnchor.parentNode.insertBefore(specs,     mobileAnchor.nextSibling);
      mobileAnchor.parentNode.insertBefore(whyLoved,  mobileAnchor.nextSibling);
      moved = true;
    }

    function moveToDesktop() {
      if (!moved) return;
      // Restore specs first so whyLovedOrigNext (= specs node) is in the right place
      if (specsOrigNext) {
        specsOrigParent.insertBefore(specs, specsOrigNext);
      } else {
        specsOrigParent.appendChild(specs);
      }
      // Restore whyLoved before specs (whyLovedOrigNext is the specs node)
      if (whyLovedOrigNext) {
        whyLovedOrigParent.insertBefore(whyLoved, whyLovedOrigNext);
      } else {
        whyLovedOrigParent.appendChild(whyLoved);
      }
      moved = false;
    }

    function check() {
      if (window.innerWidth < BREAKPOINT) {
        moveToMobile();
      } else {
        moveToDesktop();
      }
    }

    check();
    window.addEventListener('resize', check, { passive: true });
  }

  /* ==
     FABRIC SWATCH TOOLTIPS — mobile tap-to-show
     JS computes a viewport-clamped left offset so the tooltip never causes horizontal scroll.
     == */
  var fabricTooltipTouchX = 0;
  var fabricTooltipTouchY = 0;
  var fabricTooltipDocInited = false;

  /* ==
     SIZES ACCORDION  (toggle overflow cards on "View more / Show fewer")
     == */
  function initSizesAccordion() {
    var sections = document.querySelectorAll('[data-mh-sizes-section]');
    if (!sections.length) return;

    sections.forEach(function (sec) {
      var btn = sec.querySelector('[data-mh-sizes-toggle]');
      if (!btn) return;

      btn.addEventListener('click', function () {
        var expanded = sec.classList.toggle('is-expanded');
        btn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        btn.textContent = expanded
          ? (btn.dataset.labelLess || 'Show fewer sizes')
          : (btn.dataset.labelMore || 'View more sizes');

        sec.querySelectorAll('.mh-sizes__item--overflow').forEach(function (item) {
          item.setAttribute('aria-hidden', expanded ? 'false' : 'true');
        });
      });
    });
  }

  function initFabricTooltips() {
    var roots = document.querySelectorAll('[data-custom-fabric-color-options-root]');
    if (!roots.length) return;

    if (!fabricTooltipDocInited) {
      fabricTooltipDocInited = true;
      document.addEventListener('touchstart', function (e) {
        var touch = e.touches[0];
        if (touch) {
          fabricTooltipTouchX = touch.clientX;
          fabricTooltipTouchY = touch.clientY;
        }
        if (!e.target.closest('.custom-fabric-color-options__swatch-hit')) {
          document.querySelectorAll('.custom-fabric-color-options__swatch-hit.is-tooltip-open').forEach(function (h) {
            h.classList.remove('is-tooltip-open');
          });
        }
      }, { passive: true });
    }

    roots.forEach(function (root) {
      if (root._mhTooltipInited) return;
      root._mhTooltipInited = true;

      root.addEventListener('touchend', function (e) {
        if (!window.matchMedia('(max-width: 640px)').matches) return;
        var touch = e.changedTouches[0];
        if (!touch) return;
        if (Math.abs(touch.clientX - fabricTooltipTouchX) > 8 || Math.abs(touch.clientY - fabricTooltipTouchY) > 8) return;

        var hit = e.target.closest('.custom-fabric-color-options__swatch-hit');
        if (!hit) return;

        var isOpen = hit.classList.contains('is-tooltip-open');
        document.querySelectorAll('.custom-fabric-color-options__swatch-hit.is-tooltip-open').forEach(function (h) {
          h.classList.remove('is-tooltip-open');
        });

        if (!isOpen) {
          var tooltip = hit.querySelector('.custom-fabric-color-options__swatch-tooltip');
          if (tooltip) {
            var hitRect = hit.getBoundingClientRect();
            var tooltipWidth = tooltip.offsetWidth || Math.min(160, window.innerWidth - 16);
            var idealLeft = hitRect.left + hitRect.width / 2 - tooltipWidth / 2;
            var clampedLeft = Math.max(8, Math.min(idealLeft, window.innerWidth - tooltipWidth - 8));
            tooltip.style.left = (clampedLeft - hitRect.left) + 'px';
            tooltip.style.right = 'auto';
          }
          hit.classList.add('is-tooltip-open');
        }
      }, { passive: true });
    });
  }

  /* ==
     FOOTER ACCORDION  (mobile collapsible columns)
     == */
  function initFooterAccordion() {
    document.querySelectorAll('.mh-footer__col-toggle').forEach(function (btn) {
      if (btn._mhFooterBound) return;
      btn._mhFooterBound = true;
      btn.addEventListener('click', function () {
        var expanded = this.getAttribute('aria-expanded') === 'true';
        this.setAttribute('aria-expanded', expanded ? 'false' : 'true');
      });
    });
  }

  /* ==
     FOOTER "VIEW MORE"  (desktop link-list columns with 8+ links)
     Each button only toggles its own column, so multiple Link list
     blocks in the same footer act independently.
     == */
  function initFooterViewMore() {
    document.querySelectorAll('.mh-footer__view-more').forEach(function (btn) {
      if (btn._mhViewMoreBound) return;
      btn._mhViewMoreBound = true;
      btn.addEventListener('click', function () {
        var col = this.closest('.mh-footer__col--menu');
        var expanded = this.getAttribute('aria-expanded') === 'true';
        this.setAttribute('aria-expanded', expanded ? 'false' : 'true');
        if (col) col.classList.toggle('is-expanded', !expanded);
        var label = this.querySelector('.mh-footer__view-more-text');
        if (label) label.textContent = expanded ? 'View more' : 'View less';
      });
    });
  }

  /* ==
     GOOGLE REVIEWS  (sections/mh-google-reviews.liquid)
     Google-reviews slider: Read more / Read less, drag-to-scroll,
     card → review link, prev/next controls. Mirrors Trustpilot Slider.
     Split out of the former "Showroom + Google (M&H)" section.
     == */
  function initGoogleReviews() {
    var DRAG_THRESHOLD = 6;

    document.querySelectorAll('.mh-greviews').forEach(function (root) {
      if (root._mhGrBound) return;
      root._mhGrBound = true;

      var track = root.querySelector('[data-mh-gr-scroll]');

      /* Read more / Read less */
      root.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-mh-gr-readmore]');
        if (!btn) return;
        e.stopPropagation();
        var body = btn.closest('[data-mh-gr-body]');
        if (!body) return;
        var expanded = btn.getAttribute('aria-expanded') === 'true';
        var shortEl = body.querySelector('.mh-greviews__body-short');
        var fullEl = body.querySelector('.mh-greviews__body-full');
        if (shortEl) shortEl.hidden = !expanded;
        if (fullEl) fullEl.hidden = expanded;
        btn.setAttribute('aria-expanded', String(!expanded));
        btn.textContent = expanded ? 'Read more' : 'Read less';
      });

      if (!track) return;

      /* Open the reviewer's Google review on card click (ignore Read more) */
      track.addEventListener('click', function (e) {
        if (e.target.closest('.mh-greviews__read-more')) return;
        var card = e.target.closest('.mh-greviews__card');
        if (!card || card._mhDragMoved) return;
        var url = card.getAttribute('data-mh-gr-review-url');
        if (url) window.open(url, '_blank', 'noopener');
      });

      /* Drag-to-scroll (mouse) */
      var isDown = false, startX = 0, startScroll = 0, dragMoved = false;
      track.addEventListener('mousedown', function (e) {
        isDown = true;
        dragMoved = false;
        track.classList.add('is-dragging');
        startX = e.pageX - track.offsetLeft;
        startScroll = track.scrollLeft;
      });
      track.addEventListener('mouseleave', function () { isDown = false; track.classList.remove('is-dragging'); });
      track.addEventListener('mouseup', function () { isDown = false; track.classList.remove('is-dragging'); });
      track.addEventListener('mousemove', function (e) {
        if (!isDown) return;
        e.preventDefault();
        var walk = e.pageX - track.offsetLeft - startX;
        if (Math.abs(walk) > DRAG_THRESHOLD) dragMoved = true;
        track.scrollLeft = startScroll - walk;
      });

      /* Touch swipe tracking (native scroll handles the motion) */
      var tStartX = 0, tStartY = 0;
      track.addEventListener('touchstart', function (e) {
        dragMoved = false;
        tStartX = e.touches[0].pageX;
        tStartY = e.touches[0].pageY;
      }, { passive: true });
      track.addEventListener('touchmove', function (e) {
        var t = e.touches[0];
        if (Math.abs(t.pageX - tStartX) > DRAG_THRESHOLD || Math.abs(t.pageY - tStartY) > DRAG_THRESHOLD) dragMoved = true;
      }, { passive: true });

      /* Swallow the click that immediately follows a drag/swipe */
      track.addEventListener('click', function (e) {
        var card = e.target.closest('.mh-greviews__card');
        if (card) card._mhDragMoved = dragMoved;
        if (dragMoved) { e.stopPropagation(); e.preventDefault(); }
      }, true);

      /* Prev / next */
      var prev = root.querySelector('[data-mh-gr-prev]');
      var next = root.querySelector('[data-mh-gr-next]');

      function step() {
        var card = track.querySelector('.mh-greviews__card');
        if (!card) return track.clientWidth * 0.8;
        var gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap || '20') || 20;
        return card.getBoundingClientRect().width + gap;
      }
      function sync() {
        var maxScroll = track.scrollWidth - track.clientWidth - 1;
        if (prev) prev.disabled = track.scrollLeft <= 0;
        if (next) next.disabled = track.scrollLeft >= maxScroll;
      }
      if (prev) prev.addEventListener('click', function () { track.scrollBy({ left: -step(), behavior: 'smooth' }); });
      if (next) next.addEventListener('click', function () { track.scrollBy({ left: step(), behavior: 'smooth' }); });
      track.addEventListener('scroll', sync, { passive: true });
      window.addEventListener('resize', sync);
      sync();
    });
  }

  function initCollectionHeaderRail() {
    document.querySelectorAll('[data-mh-cth-rail]').forEach(function (root) {
      if (root._mhCthBound) return;
      root._mhCthBound = true;

      var track = root.querySelector('[data-mh-cth-rail-track]');
      var prev = root.querySelector('[data-mh-cth-rail-prev]');
      var next = root.querySelector('[data-mh-cth-rail-next]');
      if (!track) return;

      function step() {
        var item = track.querySelector('.mh-collection-type-header__rail-item');
        if (!item) return track.clientWidth * 0.8;
        var gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap || '16') || 16;
        return item.getBoundingClientRect().width + gap;
      }
      function sync() {
        var maxScroll = track.scrollWidth - track.clientWidth - 1;
        if (prev) prev.disabled = track.scrollLeft <= 0;
        if (next) next.disabled = track.scrollLeft >= maxScroll;
      }
      if (prev) prev.addEventListener('click', function () { track.scrollBy({ left: -step(), behavior: 'smooth' }); });
      if (next) next.addEventListener('click', function () { track.scrollBy({ left: step(), behavior: 'smooth' }); });
      track.addEventListener('scroll', sync, { passive: true });
      window.addEventListener('resize', sync);
      sync();
    });
  }

  /* ==
     SWATCH CONTEXT RELAY  (colour + fabric query params → product card links)
     A "Shop by colour" swatch (sections/mh-colours.liquid) links to its
     collection with ?colour=<Title override>&fabric=<Sub-label> appended.
     Shopify Liquid can't read that incoming query string on the collection
     page, so this rewrites every product card link on the page to carry the
     same two params forward onto the PDP, where
     assets/custom-fabric-color-options.js reads them to pre-select the
     matching fabric material + colour swatch. No-ops when neither param is
     present, and re-runs safely (idempotent) on Shopify section reloads.
     A MutationObserver also relays onto any product cards added later, e.g.
     by infinite-scroll/paginated collection grids. Deliberately does NOT
     touch the "Select sofa type" sibling links — see initSofaTypeLinkSync
     below, which owns those exclusively. (They used to be included here too,
     but this observer's broad document.body/subtree watch fires on unrelated
     mutations elsewhere in the page — e.g. the accordion layout's selected-
     swatch preview clone — which kept re-stamping those links back to this
     function's one-time-captured colour/fabric, silently overwriting whatever
     the shopper had actually picked after landing.)
     == */
  function initSwatchContextRelay() {
    var params;
    try {
      params = new URLSearchParams(window.location.search);
    } catch (e) {
      return;
    }
    var colour = params.get('colour');
    var fabric = params.get('fabric');
    if (!colour && !fabric) return;

    function relay(root) {
      (root || document).querySelectorAll('.mh-collection-card a[href*="/products/"]').forEach(function (link) {
        try {
          var url = new URL(link.getAttribute('href'), window.location.origin);
          if (colour) url.searchParams.set('colour', colour);
          if (fabric) url.searchParams.set('fabric', fabric);
          link.setAttribute('href', url.pathname + url.search + url.hash);
        } catch (e) {
          /* malformed href — leave untouched */
        }
      });
    }

    relay();

    if (window._mhSwatchRelayObserver) return; // one observer per page load is enough
    window._mhSwatchRelayObserver = new MutationObserver(function (mutations) {
      var added = mutations.some(function (m) { return m.addedNodes && m.addedNodes.length; });
      if (added) relay();
    });
    window._mhSwatchRelayObserver.observe(document.body, { childList: true, subtree: true });
  }

  /* ==
     SOFA TYPE LINK SYNC  (colour + fabric → "Select sofa type" links)
     Sole owner of the query params on .sofa-type-options__grid sibling links
     (snippets/sofa-type-selector.liquid) — nothing else may write to them, to
     avoid the stale-overwrite race this replaced (see the note on
     initSwatchContextRelay above).

     1) Once on load, applies whatever ?colour=&fabric= this page itself was
        opened with, so the links are correct even on a product that has a
        "Select sofa type" block but no fabric/colour picker to fire the
        event below.
     2) From then on, assets/custom-fabric-color-options.js dispatches a
        "cffco:selection" event on document whenever the fabric material or
        colour swatch changes — including once on initial load, after any
        query-string preselect has resolved — and this keeps the links synced
        to that *live* selection, overriding the initial query string once the
        shopper actually picks something different. So clicking through to a
        sibling product (different seat count/shape) opens with whatever is
        currently selected, not necessarily what was in this page's own URL.

     No-op on pages without a "Select sofa type" block.
     == */
  function initSofaTypeLinkSync() {
    var links = document.querySelectorAll('.sofa-type-options__option[href]');
    if (!links.length) return;

    function apply(fabric, colour) {
      if (!fabric && !colour) return;
      links.forEach(function (link) {
        try {
          var url = new URL(link.getAttribute('href'), window.location.origin);
          if (fabric) url.searchParams.set('fabric', fabric);
          if (colour) url.searchParams.set('colour', colour);
          link.setAttribute('href', url.pathname + url.search + url.hash);
        } catch (err) {
          /* malformed href — leave untouched */
        }
      });
    }

    try {
      var params = new URLSearchParams(window.location.search);
      apply(params.get('fabric'), params.get('colour'));
    } catch (e) {
      /* ignore — falls through to the live event below */
    }

    document.addEventListener('cffco:selection', function (e) {
      var detail = e.detail || {};
      apply(detail.fabric, detail.colour);
    });
  }

  function init() {
    setLayoutVars();
    initTopBarSlider();
    initHeader();
    initSubmenus();
    initDrawer();
    initHero();
    initCustomerGallery();
    initCustomizeFabrics();
    initBottomCta();
    initPdpMobileReorder();
    initFabricTooltips();
    initSizesAccordion();
    initGoogleReviews();
    initCollectionHeaderRail();
    initSwatchContextRelay();
    initSofaTypeLinkSync();
    initFooterAccordion();
    initFooterViewMore();

    window.addEventListener('resize', setLayoutVars, { passive: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
